import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Zap, Stethoscope, Clock, FileText, Users, Shield, 
  Hospital, AlertTriangle, Activity
} from 'lucide-react';
import ShaderBackground from './ShaderBackground';

const ModeSelection = ({ onModeSelect }) => {
  const [selectedMode, setSelectedMode] = useState(null);

  // Logo URLs
  const uofaLogo = "https://customer-assets.emergentagent.com/job_burnview/artifacts/waasx6tm_image.png";
  const skinopathyLogo = "https://customer-assets.emergentagent.com/job_burnview/artifacts/jdhals1x_image.png";

  const modes = [
    {
      id: 'emergency',
      title: 'Emergency Assessment',
      subtitle: 'ABA/ATLS Burn Protocol',
      description: 'Rapid assessment following Advanced Trauma Life Support guidelines for acute burn injuries',
      icon: <Zap className="w-8 h-8" />,
      features: [
        'Primary Survey (ABCDE)',
        'Secondary Survey & Mechanism',
        'TBSA Assessment & Photography', 
        'Fluid Resuscitation Calculator',
        'Wound Care Protocols'
      ],
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200',
      urgency: 'IMMEDIATE',
      timeframe: '5-10 minutes',
      users: 'Emergency Department, Paramedics, First Responders'
    },
    {
      id: 'consultation',
      title: 'Medical Consultation',
      subtitle: 'Comprehensive Assessment',  
      description: 'Complete medical consultation with detailed patient history, examination, and treatment planning',
      icon: <Stethoscope className="w-8 h-8" />,
      features: [
        'Consulting Physician Details',
        'Comprehensive Patient History',
        'Physical Examination',
        'Treatment Planning',
        'Professional Reports (PDF/Word)'
      ],
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600', 
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      urgency: 'SCHEDULED',
      timeframe: '5-10 minutes',
      users: 'Burn Specialists, Plastic Surgeons, Clinic Staff'
    }
  ];

  const handleModeSelect = (mode) => {
    setSelectedMode(mode.id);
    if (onModeSelect) {
      onModeSelect(mode.id, mode);
    }
  };

  return (
    <div className="min-h-screen bg-black relative">
      {/* Shader Animation Background */}
      <ShaderBackground />
      
      {/* Dark overlay to improve text visibility */}
      <div className="absolute inset-0 bg-black/70 z-0"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-6xl relative z-10">
        
        {/* Header with Logos */}
        <div className="text-center mb-12">
          {/* Logos Row */}
          <div className="flex items-center justify-center gap-8 mb-6">
            <img 
              src={skinopathyLogo} 
              alt="SKINOPATHY" 
              className="h-20 object-contain"
            />
            <img 
              src={uofaLogo} 
              alt="University of Alberta" 
              className="h-20 object-contain"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-2">SKINOPATHY</h1>
          <p className="text-xl text-zinc-300 mb-4">
            Medical Burn Assessment & Treatment Platform
          </p>
          <p className="text-sm text-zinc-400 mb-4">
            Select your clinical workflow based on the assessment type and urgency
          </p>
          
          {/* Credit Line */}
          <p className="text-sm text-zinc-500 italic">
            Designed by Drs. Hong and Wong, Our Clinical Team and AI & IT Team
          </p>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {modes.map((mode) => (
            <Card 
              key={mode.id}
              className={`cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 bg-zinc-900 ${
                selectedMode === mode.id 
                  ? `${mode.borderColor} shadow-xl scale-105` 
                  : 'border-zinc-700 hover:border-zinc-600'
              }`}
              onClick={() => setSelectedMode(mode.id)}
            >
              <CardHeader className="bg-zinc-800">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${mode.color} text-white`}>
                      {mode.icon}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-white">
                        {mode.title}
                      </CardTitle>
                      <p className="text-sm font-medium text-zinc-300">
                        {mode.subtitle}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                      mode.urgency === 'IMMEDIATE' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {mode.urgency === 'IMMEDIATE' ? (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      ) : (
                        <Clock className="w-3 h-3 mr-1" />
                      )}
                      {mode.urgency}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6">
                <p className="text-zinc-300 mb-6 leading-relaxed">
                  {mode.description}
                </p>

                {/* Key Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Key Features
                  </h4>
                  <ul className="space-y-2">
                    {mode.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-zinc-300">
                        <div className={`w-1.5 h-1.5 rounded-full ${mode.color}`}></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Workflow Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-zinc-800 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</span>
                    </div>
                    <p className="text-sm font-medium text-white">{mode.timeframe}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-zinc-400" />
                      <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Primary Users</span>
                    </div>
                    <p className="text-sm font-medium text-white">{mode.users}</p>
                  </div>
                </div>

                {/* Select Button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModeSelect(mode);
                  }}
                  className={`w-full ${mode.color} ${mode.hoverColor} text-white font-semibold py-3 transition-all duration-300 ${
                    selectedMode === mode.id ? 'shadow-lg' : ''
                  }`}
                  size="lg"
                >
                  {selectedMode === mode.id ? (
                    <>
                      <Activity className="w-4 h-4 mr-2" />
                      Starting {mode.title}...
                    </>
                  ) : (
                    <>
                      Start {mode.title}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Safety Notice */}
        <Card className="bg-yellow-900/30 border-yellow-600">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-200 mb-2">
                  Clinical Safety Notice
                </h4>
                <p className="text-sm text-yellow-300 leading-relaxed">
                  This application provides clinical decision support and should be used by qualified healthcare professionals. 
                  All assessments should be validated with clinical judgment and institutional protocols. 
                  In life-threatening emergencies, prioritize immediate patient care over documentation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModeSelection;